export default class BaseTabs {
  constructor(componentName) {
    this.componentName = componentName;
    this.prefix = componentName;
    this.elements = document.querySelectorAll(`[data-component="${componentName}"]`);

    if (this.elements.length === 0) return;

    this.elements.forEach((el) => this.initializeElement(el));
    this.handleNavOverflow();
    window.addEventListener('resize', () => this.handleNavOverflow());
  }

  initializeElement(element) {
    const ctx = this.getContext(element);

    this.setupTabNavigation(ctx);
    this.setupCustomSelect(ctx);
  }

  getContext(element) {
    const p = this.prefix;

    return {
      element,
      navItems: element.querySelectorAll(`.${p}__nav-item`),
      panels: element.querySelectorAll(`.${p}__panel`),
      selectButton: element.querySelector(`.${p}__select`),
      selectDropdown: element.querySelector(`.${p}__select-dropdown`),
      selectOptions: element.querySelectorAll(`.${p}__select-option`),
    };
  }

  /* ---- Tab navigation ---- */

  setupTabNavigation(ctx) {
    ctx.navItems.forEach((navItem) => {
      navItem.addEventListener('click', (e) => {
        this.switchTab(e.currentTarget.dataset.tab, ctx);
      });
    });
  }

  switchTab(tabIndex, ctx) {
    this.deactivateAll(ctx);
    this.activateTab(tabIndex, ctx);
    this.activatePanel(tabIndex, ctx);

    if (ctx.selectButton && ctx.selectOptions.length) {
      this.updateSelectOptions(ctx.selectOptions, tabIndex);
      this.updateSelectDisplay(ctx, tabIndex);
    }
  }

  deactivateAll({ navItems, panels }) {
    navItems.forEach((item) => item.classList.remove('is-active'));
    panels.forEach((panel) => panel.classList.remove('is-active'));
  }

  activateTab(tabIndex, { element }) {
    const tab = element.querySelector(`[data-tab="${tabIndex}"]`);
    if (!tab) return;

    tab.classList.add('is-active');
    this.scrollTabIntoView(tab, element);
  }

  activatePanel(tabIndex, { element }) {
    const panel = element.querySelector(`[data-panel="${tabIndex}"]`);
    if (panel) panel.classList.add('is-active');
  }

  scrollTabIntoView(tab, element) {
    const nav = element.querySelector(`.${this.prefix}__nav`);
    if (!nav) return;

    const navRect = nav.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();

    const isFullyVisible =
      tabRect.left >= navRect.left && tabRect.right <= navRect.right;

    if (!isFullyVisible) {
      nav.scrollTo({
        left: nav.scrollLeft + tabRect.left - navRect.left - (navRect.width - tabRect.width) / 2,
        behavior: 'smooth',
      });
    }
  }

  /* ---- Custom select (mobile dropdown) ---- */

  setupCustomSelect({ element, navItems, panels, selectButton, selectDropdown, selectOptions }) {
    if (!selectButton || !selectDropdown) return;

    selectButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleDropdown(selectButton, selectDropdown);
    });

    const ctx = { element, navItems, panels, selectButton, selectDropdown, selectOptions };

    selectOptions.forEach((option) => {
      option.addEventListener('click', (e) => {
        this.handleSelectChange(e.currentTarget.dataset.value, ctx);
      });
    });

    document.addEventListener('click', (e) => {
      if (!element.contains(e.target)) {
        this.closeDropdown(selectButton, selectDropdown);
      }
    });
  }

  toggleDropdown(selectButton, selectDropdown) {
    const isExpanded = selectButton.getAttribute('aria-expanded') === 'true';
    selectButton.setAttribute('aria-expanded', !isExpanded);
    selectDropdown.classList.toggle('is-open');
  }

  closeDropdown(selectButton, selectDropdown) {
    selectButton.setAttribute('aria-expanded', 'false');
    selectDropdown.classList.remove('is-open');
  }

  handleSelectChange(value, ctx) {
    this.deactivateAll(ctx);
    this.updateSelectOptions(ctx.selectOptions, value);
    this.activateTab(value, ctx);
    this.activatePanel(value, ctx);
    this.updateSelectDisplay(ctx, value);
    this.closeDropdown(ctx.selectButton, ctx.selectDropdown);
  }

  updateSelectOptions(selectOptions, value) {
    selectOptions.forEach((option) => {
      option.setAttribute('aria-selected', String(option.dataset.value === value));
    });
  }

  updateSelectDisplay({ selectButton, selectOptions }, value) {
    const selected = Array.from(selectOptions).find((opt) => opt.dataset.value === value);
    if (!selected) return;

    const text = selected.querySelector('span')?.textContent || '';
    const content = selectButton.querySelector(`.${this.prefix}__select-content`);
    if (content) {
      content.innerHTML = `<span class="${this.prefix}__select-text">${text}</span>`;
    }
  }

  /* ---- Nav overflow detection ---- */

  handleNavOverflow() {
    this.elements.forEach((element) => {
      const nav = element.querySelector(`.${this.prefix}__nav`);
      const content = element.querySelector(`.${this.prefix}__content`);

      if (!nav || !content) return;

      content.classList.toggle('nav-overflow', nav.scrollWidth > nav.clientWidth + 1);
    });
  }
}
